import React, { useState, useMemo } from 'react'
import { library }                  from '@fortawesome/fontawesome-svg-core'
import { fas }                      from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon }          from '@fortawesome/react-fontawesome'
import { patchRequest, deleteRequest } from '../helpers/fetch'
import DraftRecordsModal                from './DraftRecordsModal'

library.add(fas)

const formatEuros = cents => {
  const value = (cents || 0) / 100
  return value.toLocaleString(I18n.locale || 'fr', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

const StatusPill = ({ active }) => {
  const key = active ? 'active' : 'inactive'
  const classes = active
    ? 'text-emerald-700 bg-emerald-100 border-emerald-300'
    : 'text-gray-600 bg-gray-100 border-gray-300'
  return (
    <span className={`text-[11px] capitalize px-2.5 py-0.5 rounded-full border ${classes}`}>
      {I18n.t(`validation_workflows.status.${key}`)}
    </span>
  )
}

const Card = ({ workflow, currencySymbol, objectType, onToggle, onDuplicate, onDelete }) => {
  const isActive    = workflow.active
  const amountBased = objectType !== 'workorder'
  const cardBg      = isActive ? 'bg-white' : 'bg-gray-50 opacity-85'
  const dotBg       = isActive ? 'bg-emerald-500' : 'bg-gray-300'
  return (
    <div className={`flex items-center gap-4 px-5 py-4 border border-gray-200 rounded-lg transition-shadow hover:shadow-sm ${cardBg}`}>
      <div className={`shrink-0 w-2 h-2 rounded-full ${dotBg}`} />
      <a href={workflow.editPath} className='flex-1 min-w-0' style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }} data-turbolinks='false'>
        <div className='flex items-center gap-2.5 mb-1'>
          <span className='font-semibold text-[15px]'>{workflow.name || I18n.t('validation_workflows.untitled')}</span>
          <StatusPill active={isActive} />
        </div>
        <div className='flex items-center gap-1.5 text-[13px] text-gray-500'>
          {amountBased && (
            <>
              <span>{`≥ ${formatEuros(workflow.triggerAmountCents)} ${currencySymbol || ''}`}</span>
              <span className='text-gray-300'>·</span>
            </>
          )}
          <span>{I18n.t('validation_workflows.steps_count', { count: workflow.approvalStepsCount })}</span>
          <span className='text-gray-300'>·</span>
          <span>{workflow.selfApproval
            ? I18n.t('validation_workflows.self_approval_on')
            : I18n.t('validation_workflows.self_approval_off')}
          </span>
          {(workflow.categoryNames || []).length > 0 && (
            <>
              <span className='text-gray-300'>·</span>
              <span>{(workflow.categoryNames || []).join(', ')}</span>
            </>
          )}
        </div>
      </a>
      <div className='flex items-center gap-1'>
        <button className='btn btn-link btn-sm text-gray-500 hover:text-blue-600 hover:underline' onClick={() => onToggle(workflow)}>
          {isActive ? I18n.t('validation_workflows.deactivate') : I18n.t('validation_workflows.activate')}
        </button>
        <button
          className = 'btn btn-link btn-sm text-gray-500 hover:text-blue-600'
          onClick   = {() => onDuplicate(workflow)}
          title     = {I18n.t('validation_workflows.duplicate')}
        >
          <FontAwesomeIcon icon='copy' />
        </button>
        <button
          className = 'btn btn-link btn-sm text-danger hover:text-red-700'
          onClick   = {() => onDelete(workflow)}
          title     = {I18n.t('validation_workflows.delete')}
        >
          <FontAwesomeIcon icon='trash' />
        </button>
      </div>
    </div>
  )
}

const SubTab = ({ active, onClick, label, count }) => {
  const base   = 'border px-3 py-1.5 rounded-md cursor-pointer text-sm'
  const state  = active
    ? 'bg-gray-100 text-gray-900 border-gray-200 font-semibold'
    : 'bg-transparent border-transparent text-gray-600'
  return (
    <button className={`${base} ${state}`} onClick={onClick}>
      {label}{' '}
      <span className='ml-1.5 bg-gray-200 text-gray-700 rounded-[10px] px-2 py-0.5 text-xs'>{count}</span>
    </button>
  )
}

const Index = ({
  objectType,
  workflows: initialWorkflows,
  counts: initialCounts,
  search: initialSearch,
  statusFilter: initialStatusFilter,
  currencySymbol,
  newPath
}) => {
  const [workflows, setWorkflows] = useState(initialWorkflows || [])
  const [search, setSearch]       = useState(initialSearch || '')
  const [filter, setFilter]       = useState(initialStatusFilter || 'all')
  const [pendingToggle, setPendingToggle] = useState(null)

  // Counts derived from local state so toggle/delete stay in sync without a reload.
  const counts = useMemo(() => ({
    all:      workflows.length,
    active:   workflows.filter(w => w.active).length,
    inactive: workflows.filter(w => !w.active).length
  }), [workflows])

  const filtered = useMemo(() => {
    let list = workflows
    if (filter === 'active')   list = list.filter(w => w.active)
    if (filter === 'inactive') list = list.filter(w => !w.active)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(w => (w.name || '').toLowerCase().includes(q))
    }
    return list.slice().sort((a, b) =>
      (a.triggerAmountCents - b.triggerAmountCents) || (a.id - b.id)
    )
  }, [workflows, filter, search])

  const onToggle = (workflow, draftRecords) => {
    // Optimistic flip; revert if the server rejects (e.g. in-flight records).
    const previousActive = workflow.active
    const revert = () => setWorkflows(prev => prev.map(w => w.id === workflow.id ? { ...w, active: previousActive } : w))
    setWorkflows(prev => prev.map(w => w.id === workflow.id ? { ...w, active: !previousActive } : w))
    patchRequest({ url: workflow.toggleActivePath, body: { draft_records: draftRecords } }).then(res => {
      if (res && res.caught_drafts_count) {
        revert()
        setPendingToggle({ workflow, count: res.caught_drafts_count })
        return
      }
      setPendingToggle(null)
      if (res && Array.isArray(res.errors) && res.errors.length > 0) {
        revert()
        window.alert(res.errors.join('\n'))
      }
    })
  }

  const onDuplicate = workflow => {
    patchRequest({ url: workflow.duplicatePath }).then(res => {
      if (res && res.redirect_to) {
        window.location.href = res.redirect_to
        return
      }
      window.location.reload()
    })
  }

  const onDelete = workflow => {
    if (!window.confirm(I18n.t('validation_workflows.confirm_delete'))) return
    deleteRequest(workflow.destroyPath).then(res => {
      if (res && Array.isArray(res.errors) && res.errors.length > 0) {
        window.alert(res.errors.join('\n'))
        return
      }
      setWorkflows(prev => prev.filter(w => w.id !== workflow.id))
    })
  }

  return (
    <div className='py-6 px-8'>
      <div className='flex justify-between items-start mb-6'>
        <div>
          <h4 className='m-0 font-bold'>{I18n.t(`validation_workflows.title.${objectType}`)}</h4>
          <div className='text-muted'>{I18n.t(`validation_workflows.subtitle.${objectType}`)}</div>
        </div>
        <a href={newPath} className='btn btn-primary' data-turbolinks='false'>
          <FontAwesomeIcon icon='plus' /> {I18n.t('validation_workflows.new_workflow')}
        </a>
      </div>

      <div className='flex items-center gap-3 mb-4 flex-wrap'>
        <SubTab
          active = {filter === 'all'}
          onClick = {() => setFilter('all')}
          label   = {I18n.t('validation_workflows.filters.all')}
          count   = {counts.all}
        />
        <SubTab
          active = {filter === 'active'}
          onClick = {() => setFilter('active')}
          label   = {I18n.t('validation_workflows.filters.active')}
          count   = {counts.active}
        />
        <SubTab
          active = {filter === 'inactive'}
          onClick = {() => setFilter('inactive')}
          label   = {I18n.t('validation_workflows.filters.inactive')}
          count   = {counts.inactive}
        />
        <div className='flex-1 min-w-[220px] ml-auto max-w-[360px] relative'>
          <input
            type        = 'text'
            value       = {search}
            onChange    = {e => setSearch(e.target.value)}
            placeholder = {I18n.t('validation_workflows.search_placeholder')}
            className   = 'form-control form-control-sm w-full pr-8'
          />
          <FontAwesomeIcon
            icon='search'
            className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className='px-5 py-10 text-center text-gray-400 border border-dashed border-gray-200 rounded-lg'>
          <FontAwesomeIcon icon='inbox' size='2x' />
          <p className='my-3'>{I18n.t('validation_workflows.no_workflows')}</p>
        </div>
      ) : (
        <div className='flex flex-col gap-3'>
          {filtered.map(wf => (
            <Card
              key            = {wf.id}
              workflow       = {wf}
              currencySymbol = {currencySymbol}
              objectType     = {objectType}
              onToggle       = {onToggle}
              onDuplicate    = {onDuplicate}
              onDelete       = {onDelete}
            />
          ))}
        </div>
      )}

      <DraftRecordsModal
        count      = {pendingToggle ? pendingToggle.count : 0}
        objectType = {objectType}
        saving     = {!!(pendingToggle && pendingToggle.saving)}
        onCancel   = {() => setPendingToggle(null)}
        onConfirm  = {choice => {
          setPendingToggle({ ...pendingToggle, saving: true })
          onToggle(pendingToggle.workflow, choice)
        }}
      />
    </div>
  )
}

export default Index
